import { useClerk } from "@clerk/react";
import { Link } from "@tanstack/react-router";

import { useUser } from "@/web/auth/useUser";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/web/components/primitives/dropdown-menu";
import { AuthAvatar } from "@/web/components/styled/AuthAvatar";

// ---------------------------------------------------------------------------
// The signed-in account menu, in ONE place.
//
// This used to be copy-pasted three times — `AppNavbar`'s desktop menu, its
// mobile hamburger, and the TTW hero header's floating menu — and they had
// already drifted: the TTW copy was missing the portfolio link entirely, so
// the link a user found on `/browse` vanished on an offering page. Any surface
// that shows the avatar now gets the same rows by construction.
// ---------------------------------------------------------------------------

/**
 * The destination rows, shared by the desktop dropdown and the mobile menu.
 *
 * Exported as data rather than JSX so `MobileMenu` can splice them into its own
 * `DropdownMenuGroup` alongside the nav links, without nesting a second menu.
 */
export const ACCOUNT_MENU_LINKS = [
  // "Edit account", not "Profile": this row is the ONLY route to the personal
  // info form now that the investor dashboard's own "Edit your info" link is
  // gone, so it names the action rather than the destination.
  { to: "/profile/user", label: "Edit account" },
  { to: "/profile/smb", label: "Business Dashboard" },
  { to: "/profile/inv", label: "Portfolio" },
] as const;

export function AccountMenuLinkItems() {
  return (
    <>
      {ACCOUNT_MENU_LINKS.map((item) => (
        <DropdownMenuItem key={item.to} asChild>
          <Link to={item.to}>{item.label}</Link>
        </DropdownMenuItem>
      ))}
    </>
  );
}

/**
 * Sign out, everywhere it appears.
 *
 * `client.destroy()` before `signOut()` tears down the whole Clerk client, and
 * the hard `window.location` assignment (rather than a router navigation) drops
 * every in-memory subscription with it — a soft navigation would leave the
 * previous user's SpacetimeDB rows cached.
 */
export function SignOutMenuItem({ onBeforeSignOut }: { onBeforeSignOut?: () => void }) {
  const { signOut, client } = useClerk();

  return (
    <DropdownMenuItem
      variant="destructive"
      onSelect={async () => {
        onBeforeSignOut?.();
        await client?.destroy?.();
        await signOut();
        window.location.href = "/";
      }}
    >
      Sign out
    </DropdownMenuItem>
  );
}

/**
 * Avatar + dropdown. Callers must wrap in `<Suspense>` — `useUser` (inside
 * `AuthAvatar`) suspends until the row loads.
 *
 * `avatarClassName` is the only styling seam: the TTW hero renders this over an
 * arbitrary owner-uploaded photo and needs the translucent treatment, while the
 * app nav wants the default. The MENU itself is deliberately not themeable —
 * it's a floating popover on an opaque surface in both cases.
 */
export function SignedInAccountMenu({
  avatarClassName,
  open,
  onOpenChange,
}: {
  avatarClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const user = useUser();

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <AuthAvatar className={avatarClassName} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        <DropdownMenuLabel className="truncate font-normal text-muted-foreground">
          {user?.email ?? "Account"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <AccountMenuLinkItems />
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <SignOutMenuItem onBeforeSignOut={() => onOpenChange?.(false)} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
